import React from "react";
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import axios from "axios";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeInRight = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const ContactSection = () => {
    const [form, setForm] = React.useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${process.env.REACT_APP_MY_API}/api/contact`, form);
            setSnackbar({
                open: true,
                message: "Thank you! We'll get back to you shortly.",
                severity: "success",
            });
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to send message. Please try again later.",
                severity: "error",
            });
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact PM Painters | Book a Free Consultation</title>
                <meta name="description" content="Get in touch with PM Painters for expert advice, quotes, and consultations on your next painting project." />
                <meta name="keywords" content="contact painters India, book painting consultation, painter phone number, painting service enquiry" />
            </Helmet>

            <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
                <Box
                    component={motion.div}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        minHeight: { xs: "auto", md: 500 },
                        borderRadius: 3,
                        overflow: "hidden",
                        boxShadow: 5,
                        backgroundColor: "#fff",
                    }}
                >
                    {/* Contact Form */}
                    <Box
                        component={motion.div}
                        variants={fadeInUp}
                        sx={{
                            flex: 1,
                            p: { xs: 3, sm: 5 },
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                            Contact Us
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
                            We're here to help. Fill out the form and we'll get back to you as soon as possible.
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                margin="normal"
                                required
                                value={form.name}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                margin="normal"
                                required
                                value={form.email}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                type="tel"
                                margin="normal"
                                required
                                value={form.phone}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                label="Message"
                                name="message"
                                margin="normal"
                                multiline
                                rows={4}
                                required
                                value={form.message}
                                onChange={handleChange}
                            />
                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth
                                sx={{
                                    mt: 3,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    backgroundColor: "#1976d2",
                                    "&:hover": { backgroundColor: "#115293" },
                                }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>

                    {/* Google Map */}
                    <Box
                        component={motion.div}
                        variants={fadeInRight}
                        sx={{
                            flex: 1,
                            minHeight: { xs: 300, md: "100%" },
                            position: "relative",
                        }}
                    >
                        <iframe
                            title="PM Painters Location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3670.313225221762!2d72.55122107514255!3d23.08562697912958!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e83d0241c429d%3A0x3c320d28eb95d473!2spm.painters!5e0!3m2!1sen!2sin!4v1751126628223!5m2!1sen!2sin"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </Box>
                </Box>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default ContactSection;
